/**
 * @file useLaunchOpen.ts
 * @description «Открыть сайт» при паузе «запуск» (phase19.28 этап А; PL8,
 * PL9): сначала `GET launch-check`; всё готово — пауза снимается сразу;
 * есть жёсткие условия — чек-лист без кнопки открытия; только
 * предупреждения — чек-лист с «Открыть сайт». Отказ 422
 * `site_launch_blocked` при открытии — тот же чек-лист из `details`.
 */

import { useState } from "react";

import { useNotify } from "../../components/ui/NotifyProvider";
import { siteAccess, type SiteLaunchCheck } from "../../lib/api-site-access";
import { launchNeedsReview, parseLaunchBlocked } from "../../lib/site-access/launch-check";
import { siteAccessErrorText } from "../../lib/site-access/site-access-errors";
import { safeTimeZone } from "../../lib/site-access/site-access-time";
import type { SiteAccessHandle } from "./useSiteAccess";
import { SITE_ACCESS_DELAY_NOTE } from "./useSiteAccessActions";

export interface LaunchOpenState {
  /** Чек-лист на экране; null — окна нет. */
  check: SiteLaunchCheck | null;
  busy: boolean;
  /** Нажали «Открыть сайт»: проверка, затем открытие или чек-лист. */
  start: (closureId: string) => Promise<void>;
  /** «Открыть сайт» в окне (только без жёстких условий). */
  confirm: () => Promise<void>;
  close: () => void;
}

export function useLaunchOpen(access: SiteAccessHandle): LaunchOpenState {
  const notify = useNotify();
  const [check, setCheck] = useState<SiteLaunchCheck | null>(null);
  const [closureId, setClosureId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const zone = safeTimeZone(access.state?.timeZone);

  async function open(id: string) {
    try {
      access.apply(await siteAccess.open(id));
      setCheck(null);
      notify.success(`Магазин открыт для покупателей — ${SITE_ACCESS_DELAY_NOTE}`);
    } catch (err) {
      const blocked = parseLaunchBlocked(err);
      if (blocked) setCheck(blocked);
      else notify.error(siteAccessErrorText(err, "open", zone));
    }
  }

  async function start(id: string) {
    setClosureId(id);
    setBusy(true);
    try {
      const result = await siteAccess.launchCheck();
      if (launchNeedsReview(result)) setCheck(result);
      else await open(id);
    } catch (err) {
      notify.error(siteAccessErrorText(err, "open", zone));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!closureId || check?.blocked) return;
    setBusy(true);
    try {
      await open(closureId);
    } finally {
      setBusy(false);
    }
  }

  return { check, busy, start, confirm, close: () => setCheck(null) };
}
